/**
 * FSRS Helper - Wrapper for ts-fsrs library
 * Provides spaced repetition scheduling for flashcards
 * Adapted from Reedle for Mellow
 */

import { 
  createEmptyCard, 
  fsrs, 
  generatorParameters, 
  Rating,
  State
} from 'ts-fsrs';

// FSRS instance with default parameters
const params = generatorParameters({ 
  enable_fuzz: true,  // Add small random fuzz to intervals
  enable_short_term: true,  // Enable short-term learning steps
});
const f = fsrs(params);

// Export Rating and State enums for use in components
export { Rating, State };

// Rating labels for UI
export const RATING_LABELS = {
  [Rating.Again]: 'Again',
  [Rating.Hard]: 'Hard', 
  [Rating.Good]: 'Good',
  [Rating.Easy]: 'Easy',
};

// State labels for UI
export const STATE_LABELS = {
  [State.New]: 'New',
  [State.Learning]: 'Learning',
  [State.Review]: 'Review',
  [State.Relearning]: 'Relearning',
};

/**
 * Create a new empty card with FSRS default values
 * @param {Date} now - Optional current date
 * @returns {object} Empty card object
 */
export const createNewCard = (now = new Date()) => {
  const card = createEmptyCard(now);
  return {
    due: card.due.toISOString(),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state,
    last_review: card.last_review ? card.last_review.toISOString() : null,
  };
};

/**
 * Get scheduling options for all ratings
 * @param {object} card - Card object from database
 * @param {Date} now - Current date
 * @returns {object} Scheduling options for each rating
 */
export const getSchedulingOptions = (card, now = new Date()) => {
  // Convert DB card to ts-fsrs Card format
  const fsrsCard = {
    due: new Date(card.due),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state,
    last_review: card.last_review ? new Date(card.last_review) : undefined,
  };

  // Get scheduling for all ratings
  const scheduling = f.repeat(fsrsCard, now);
  
  // Return formatted options with intervals
  return {
    [Rating.Again]: formatSchedulingOption(scheduling[Rating.Again], now),
    [Rating.Hard]: formatSchedulingOption(scheduling[Rating.Hard], now),
    [Rating.Good]: formatSchedulingOption(scheduling[Rating.Good], now),
    [Rating.Easy]: formatSchedulingOption(scheduling[Rating.Easy], now),
  };
};

/**
 * Format a scheduling option for display
 */
const formatSchedulingOption = (option, now) => {
  const { card, log } = option;
  const interval = getIntervalDisplay(card.due, now);
  
  return {
    card: {
      due: card.due.toISOString(),
      stability: card.stability,
      difficulty: card.difficulty,
      elapsed_days: card.elapsed_days,
      scheduled_days: card.scheduled_days,
      reps: card.reps,
      lapses: card.lapses,
      state: card.state,
      last_review: now.toISOString(),
    },
    log,
    interval,
  };
};

/**
 * Format interval for display (e.g., "<1min", "10min", "4d")
 */
const getIntervalDisplay = (dueDate, now) => {
  const diffMs = dueDate - now;
  const diffMins = Math.round(diffMs / (1000 * 60));
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return '<1m';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 30) return `${diffDays}d`;
  if (diffDays < 365) return `${Math.round(diffDays / 30)}mo`;
  return `${Math.round(diffDays / 365)}y`;
};

/**
 * Check if a card is due for review
 * @param {object} card - Card object
 * @param {Date} now - Current date
 * @returns {boolean}
 */
export const isCardDue = (card, now = new Date()) => {
  return new Date(card.due) <= now;
};

/**
 * Get cards due for review, respecting daily limits
 * @param {Array} cards - All cards
 * @param {object} limits - { newCardsPerDay, reviewCardsPerDay }
 * @param {Date} now - Current date
 * @returns {Array} Cards to review
 */
export const getDueCards = (cards, limits = {}, now = new Date()) => {
  const { newCardsPerDay = 20, reviewCardsPerDay = 200 } = limits;
  
  const dueCards = cards.filter(card => isCardDue(card, now));
  
  // Separate new and review cards
  const newCards = dueCards.filter(c => c.state === State.New);
  const reviewCards = dueCards.filter(c => c.state !== State.New);
  
  // Apply limits
  const limitedNew = newCards.slice(0, newCardsPerDay);
  const limitedReview = reviewCards.slice(0, reviewCardsPerDay);
  
  // Interleave: new cards first, then reviews
  return [...limitedNew, ...limitedReview];
};

/**
 * Count cards due today
 * @param {Array} cards - All cards
 * @param {Date} now - Current date
 * @returns {number}
 */
export const countDueCards = (cards, now = new Date()) => {
  return cards.filter(card => isCardDue(card, now)).length;
};

